import RolePlayScenariosApi from '../api/roleplay-scenarios';
import RolePlaySessionsApi from '../api/roleplay-session';
import RoleplaySavedScenariosApi from '../api/roleplay-saved-scenarios';
import RoleplayCategoriesApi from '../api/roleplay-categories';
import i18n from '@/translations';

export default {
  namespaced: true,
  state: {
    roleplayCategories: [],
    selectedCategory: null,
    scenarios: [],
    roleplaySession: null,
    roleplaySessionMetadata: null,
    roleplaySessionFiles: [],
    ephemeralKey: null,
    filesConfig: {
      maxFiles: 3,
      maxFileSizeMB: 1,
      maxTextChars: 7200
    },
    savedScenarios: [],
    savedScenariosScenarioId: null,
    // Quick Roleplay. The draft session the Confirm page shows, the fields the last edit
    // changed (drives the Updated markers and the summary line), the student's
    // own description so Start over can restore it, and a one-shot alert for Intent to show
    // after a redirect (draft not found / not yours / no longer a draft).
    quickDraft: null,
    quickChanged: [],
    quickIntent: null,
    quickAlert: null,
    // The recording a "Practice as roleplay" generation is running for. Shared by every
    // recording card so only one generation can run at a time.
    videoRoleplayVideoId: null,
  },
  getters: {
    allScenarios(state) {
      return state.scenarios;
    },
    roleplayCategories(state) {
      return state.roleplayCategories;
    },
    roleplaySession(state) {
      return state.roleplaySession;
    },
    selectedCategory(state) {
      return state.selectedCategory;
    },
    roleplaySessionMetadata(state) {
      return state.roleplaySessionMetadata;
    },
    filesConfig(state) {
      return state.filesConfig;
    },
    roleplaySessionFiles(state) {
      return state.roleplaySessionFiles;
    },
    savedScenarios(state) {
      return state.savedScenarios;
    },
    quickDraft(state) {
      return state.quickDraft;
    },
    quickChanged(state) {
      return state.quickChanged;
    },
    quickIntent(state) {
      return state.quickIntent;
    },
    quickAlert(state) {
      return state.quickAlert;
    },
    videoRoleplayVideoId(state) {
      return state.videoRoleplayVideoId;
    },
  },
  actions: {
    getScenarios(context, params) {
      return new Promise((resolve, reject) => {
        RolePlayScenariosApi.getScenarios(params)
          .then((response) => {
            context.commit('SET_SCENARIOS', response);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    createRoleplaySession(context, roleplaySessionData) {
      return new Promise((resolve, reject) => {
        RolePlaySessionsApi.createRoleplaySession(roleplaySessionData)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getRoleplaySession(context, sessionId) {
      return new Promise((resolve, reject) => {
        RolePlaySessionsApi.getRoleplaySession(sessionId)
          .then((response) => {
            context.commit('SET_ROLEPLAY_SESSION', response);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getProgramAvailableCategories(context) {
      const selectedProgram = context.rootGetters['programs/selectedProgram'];

      return RoleplayCategoriesApi.getProgramAvailableCategories(selectedProgram._id)
        .then((categories) => {
          return categories;
        });
    },
    getSelectedProgramCategories(context) {
      const selectedProgram = context.rootGetters['programs/selectedProgram'];
      const language = i18n.global.locale.value;
      return RoleplayCategoriesApi.getProgramSelectedCategories(selectedProgram?._id, language)
        .then((categories) => {
          context.commit('SET_ROLEPLAY_CATEGORIES', categories);
          context.commit('SET_SELECTED_CATEGORY', null);
          return categories;
        });
    },
    getRoleplayCategory(context, categorySlug) {
      return RoleplayCategoriesApi.getRoleplayCategory(categorySlug)
        .then((category) => {
          context.commit('SET_SELECTED_CATEGORY', category);
          return category;
        });
    },
    getRoleplaySessions(context) {
      return new Promise((resolve, reject) => {
        RolePlaySessionsApi.getRoleplaySessions()
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    deleteRoleplaySession(context, sessionId) {
      return new Promise((resolve, reject) => {
        RolePlaySessionsApi.deleteRoleplaySession(sessionId)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    createOmniaCall(context, { inputSampleRate }) {
      return RolePlaySessionsApi.createOmniaCall(context.state.roleplaySession._id, { inputSampleRate });
    },
    generateContextSummary(context, fileData) {
      const program = context.rootGetters['programs/selectedProgram']?._id;

      return new Promise((resolve, reject) => {
        RolePlayScenariosApi.filesSummary({ ...fileData, program })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async startRoleplaySession(context, { placeholders, scenario, program }) {
      const userPersonaData = scenario.userPersona.find(item => item.value === placeholders.userPersona || item.label === placeholders.userPersona);

      const aiPersonaData = scenario.aiPersonaOptions.find(item => String(item._id) === String(placeholders.aiPersona));
      if (!aiPersonaData) throw new Error('Selected AI Persona not found in scenario options.');

      const communicationStyleData = scenario.communicationStyle.find(item => item.value === placeholders.communicationStyle || item.label ===placeholders.communicationStyle);

      const aiPersonalityData = scenario.aiPersonality.find(item => item.value === placeholders.aiPersonality || item.label ===placeholders.aiPersonality);

      const session = {
        scenarioId: scenario._id,
        program,
        placeholders: {
          ...placeholders,
          userPersona: userPersonaData.label,
          userPersonaComment: userPersonaData.description,
          aiPersona: aiPersonaData.label,
          aiPersonaComment: aiPersonaData.promptDescription,
          aiPersonaVoice: aiPersonaData.voice,
          communicationStyle: communicationStyleData.label,
          communicationStyleComment: communicationStyleData.description,
          aiPersonality: aiPersonalityData.label,
          aiPersonalityComment: aiPersonalityData.description,
        },
      }

      const response = await RolePlaySessionsApi.createRoleplaySession(session);

      context.commit('SET_ROLEPLAY_SESSION', response);

      return response;
    },
    async generateQuickRoleplay(context, { description, manualContext }) {
      const program = context.rootGetters['programs/selectedProgram'];

      const session = await RolePlaySessionsApi.generateQuickRoleplay({
        description,
        manualContext: manualContext || null,
        program: program?._id,
        language: i18n.global.locale.value,
      });

      context.commit('SET_QUICK_INTENT', { description, manualContext: manualContext || '' });
      context.commit('SET_QUICK_DRAFT', session);
      context.commit('SET_QUICK_CHANGED', []);

      return session;
    },
    /*
     * Practice a recorded presentation as a roleplay. The backend answers with an ordinary
     * Quick Roleplay draft, so the Confirm page consumes it untouched. The intent is cleared
     * rather than filled: the student typed nothing here, and "Start over" must return them to
     * a blank Intent page instead of restoring words they never wrote.
     */
    async generateRoleplayFromVideo(context, videoId) {
      const session = await RolePlaySessionsApi.generateRoleplayFromVideo(videoId);

      context.commit('SET_QUICK_INTENT', null);
      context.commit('SET_QUICK_DRAFT', session);
      context.commit('SET_QUICK_CHANGED', []);

      return session;
    },
    async updateQuickPlaceholders(context, { sessionId, placeholders }) {
      const session = await RolePlaySessionsApi.updateQuickPlaceholders(sessionId, placeholders);

      context.commit('SET_QUICK_DRAFT', session);
      context.commit('SET_QUICK_CHANGED', Object.keys(placeholders));

      return session;
    },
    async prepareRoleplaySession(context, sessionId) {
      const session = await RolePlaySessionsApi.prepareRoleplaySession(sessionId);

      context.commit('SET_QUICK_DRAFT', session);

      return session;
    },
    async loadQuickDraft(context, sessionId) {
      if (context.state.quickDraft?._id === sessionId) {
        return context.state.quickDraft;
      }

      const session = await RolePlaySessionsApi.getRoleplaySession(sessionId);

      context.commit('SET_QUICK_DRAFT', session);
      context.commit('SET_QUICK_CHANGED', []);

      return session;
    },
    async discardQuickDraft(context, sessionId) {
      context.commit('SET_QUICK_DRAFT', null);
      context.commit('SET_QUICK_CHANGED', []);

      // Best effort: a draft that cannot be deleted is merely orphaned, and the student has
      // already been promised their description back.
      try {
        await RolePlaySessionsApi.deleteRoleplaySession(sessionId);
      } catch (error) {
        console.error('Failed to discard quick roleplay draft:', error);
      }
    },
    async restartRoleplaySession(context, sessionId) {
      const response = await RolePlaySessionsApi.restartRoleplaySession(sessionId);

      context.commit('SET_ROLEPLAY_SESSION', response);

      return response;
    },
    async updateRoleplaySession(context, { sessionId, data }) {
      const response = await RolePlaySessionsApi.updateRoleplaySession(sessionId, data);
      context.commit('SET_ROLEPLAY_SESSION', response);
      return response;
    },
    getSavedScenarios(context, scenarioId) {
      context.commit('SET_SAVED_SCENARIOS_SCENARIO_ID', scenarioId || null);
      return new Promise((resolve, reject) => {
        RoleplaySavedScenariosApi.getAll(scenarioId)
          .then((response) => {
            context.commit('SET_SAVED_SCENARIOS', response);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    createSavedScenario(context, data) {
      return new Promise((resolve, reject) => {
        RoleplaySavedScenariosApi.create(data)
          .then((response) => {
            context.dispatch('getSavedScenarios', context.state.savedScenariosScenarioId);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    deleteSavedScenario(context, id) {
      return new Promise((resolve, reject) => {
        RoleplaySavedScenariosApi.remove(id)
          .then((response) => {
            context.dispatch('getSavedScenarios', context.state.savedScenariosScenarioId);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
  mutations: {
    SET_ROLEPLAY_CATEGORIES(state, categories) {
      state.roleplayCategories = categories;
    },
    SET_SELECTED_CATEGORY(state, category) {
      state.selectedCategory = category;
    },
    SET_SCENARIOS(state, scenarios) {
      state.scenarios = scenarios;
    },
    SET_ROLEPLAY_SESSION(state, session) {
      state.roleplaySession = session;
    },
    SET_ROLEPLAY_SESSION_METADATA(state, metadata) {
      state.roleplaySessionMetadata = metadata;
    },
    SET_EPHEMERAL_KEY(state, key) {
      state.ephemeralKey = key;
    },
    SET_FILES(state, files) {
      state.roleplaySessionFiles = files;
    },
    SET_SAVED_SCENARIOS(state, scenarios) {
      state.savedScenarios = scenarios;
    },
    SET_SAVED_SCENARIOS_SCENARIO_ID(state, scenarioId) {
      state.savedScenariosScenarioId = scenarioId;
    },
    SET_QUICK_DRAFT(state, session) {
      state.quickDraft = session;
    },
    SET_QUICK_CHANGED(state, changed) {
      state.quickChanged = Array.isArray(changed) ? changed : [];
    },
    SET_QUICK_INTENT(state, intent) {
      state.quickIntent = intent;
    },
    SET_QUICK_ALERT(state, alert) {
      state.quickAlert = alert;
    },
    SET_VIDEO_ROLEPLAY_VIDEO_ID(state, videoId) {
      state.videoRoleplayVideoId = videoId;
    },
  }
}
